import { Box, Dialog, DialogContent, Typography } from "@mui/material";
import {
  getMainCategoryInfo,
  MainCategoriesEnum,
  MODELS_DATA,
  ProductNameEnum,
  TRICKY_COMMANDS,
} from "@shared/core";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "../../styles/styles.css";
import { colors, transitions } from "../../styles/theme";
import { convertToBreakpoints, getFluidsByKeysFunc } from "../../utils/fuilds";
import { BrandLink } from "../BrandLink/BrandLink";
import { FlexOrderCarousel } from "../FlexOrderCarousel/FlexOrderCarousel";
import { Header } from "../Header/Header";
import {
  IconArrowLeft,
  IconArrowRight,
  IconArrowSharpRight,
  IconCheckMark,
  IconClose,
  IconZoomIn,
  IconZoomOut,
} from "../Icons/Icons";

import {
  useActiveBucket,
  useActiveCatalog,
  useActiveCategory,
  useActiveProduct,
  useCurrentSelection,
  useGroupBuckets,
  useMainCategoryStaticData,
  useSelectedGroup,
  useSelection,
  useTotalPrice,
} from "../../store/selectors";

import { createLead } from "../../api/crm";
import {
  ARCWARE_SHARE_ID,
  DISABLE_STREAM,
  IS_DEVELOPMENT,
  IS_TEST_LEAD,
} from "../../config";
import {
  BREAKPOINT_MOBILE,
  DRAWER_PICK_AREA_HEIGHT,
  DRAWER_PICK_HEIGHT_COLLAPSED,
  EMIT_NESTED_CATEGORIES,
  STREAM_ID,
  VAT,
} from "../../constants";
import {
  getStreamVideoElement,
  useArcwareStream,
} from "../../hooks/useArcwareStream";
import { useDebugData } from "../../hooks/useDebugData";
import { deviceStyles, useDeviceStyles } from "../../hooks/useDeviceStyles";
import { useFinalItemPrice } from "../../hooks/useFinalItemPrice";
import { useIosLandscapeChrome } from "../../hooks/useIosLandscapeChrome";
import { useAndroidFullscreen } from "../../hooks/useAndroidFullscreen";
import { useLocalPerGroupIndex } from "../../hooks/useLocalPerGroupIndex";
import { useOrientation } from "../../hooks/useOrientation";
import { useConfigStore } from "../../store";
import {
  captureVideoFrame,
  captureVideoFrameBytes,
} from "../../utils/captureVideoFrame";
import { getPriceWithCurrency } from "../../utils/formatPrice";
import { createLoaderSimulator } from "../../utils/loderSimulator";
import {
  type StreamSettleGate,
  waitForStreamSettle,
} from "../../utils/streamSettle";
import { ConditionalWrapper } from "../ConditionalWrapper/ConditionalWrapper";
import { ContactForm } from "../Forms/ContactForm/ContactForm";
import { ReceivePdfForm } from "../Forms/ReceivePdfForm/ReceivePdfForm";
import { FormValues } from "../Forms/types";
import { BottomDrawer } from "../Layout/LayoutMobile";
import { Loader } from "../Loader/Loder";
import { GoodsList } from "../parts/GoodsList";
import { GroupedCategories } from "../parts/GroupedCategories";
import { MainCategories } from "../parts/MainCategories";
import {
  RotatingBox,
  StyledApp,
  StyledIconRotate,
  StyledVideoWrapper,
} from "./App.styled";
// import { useStreamContainerSize } from "../../hooks/useStreamContainerSize";
import { useResponsiveScaler } from "../../lib/ResponsiveScaler";
import {
  appendTextureUrlsToFormData,
  getLocalStorageData,
  getSelectedImages,
  sleep,
  TextureUrls,
} from "../../utils/common";
import { scrollToElement } from "../../utils/scrollToElement";
import { FullscreenController } from "../FullscreenController/FullscreenController";
import { FullscreenGlobalStyles } from "../FullscreenController/styles";

const TIMEOUT_FORM_SUBMIT_OVERLAY = 5000;
const TIMEOUT_SCREENSHOTS = 2200;
// Re-emit change_model until Unreal confirms it. The interval is deliberately
// longer than a normal model load (~3–15s) so healthy loads are never
// re-triggered; only genuinely lost commands get resent.
const MODEL_LOAD_RETRY_MS = 12000;
// Absolute last resort: if Unreal never echoes change_model (e.g. a truly dead
// instance), reveal the stream anyway so the user isn't trapped behind an
// infinite loader. Deliberately long so a slow-but-alive reconnect always
// resolves via the echo first — the loader stays up until the yacht is there.
const MODEL_LOAD_HARD_TIMEOUT_MS = 60000;
// The retry keeps running past the hard timeout so a slow instance boot after
// a reconnect (measured ~70s) still self-heals with a late yacht — but it must
// not run forever against an instance that will never echo. 15 × 12s = 3min.
const MODEL_LOAD_MAX_ATTEMPTS = 15;
// const TIMEOUT_SCREENSHOTS = 100;

console.log("IS_TEST_LEAD", IS_TEST_LEAD);

async function onSubmitLead(values: FormValues, opts: any) {
  const { product, requestedPDF, imagesData = {} } = opts;

  const fieldsMapper: Record<string, string> = {
    first_name: "First_Name",
    last_name: "Last_Name",
    email: "Email",
    phone: "Phone",
    signed_up: "Newsletter_Signup",
  };

  const dataFromForm = Object.entries(values).reduce(
    (acc, [key, val]) => {
      const mapped = fieldsMapper[key];
      if (mapped) acc[mapped] = val as any;
      return acc;
    },
    {
      pdf_title: "PDF title",
    } as Record<string, any>,
  );

  // const extraRaw = localStorage.getItem(keyFor(product));
  // const extra = (() => {
  //   try {
  //     return JSON.parse(extraRaw || "{}");
  //   } catch {
  //     return {};
  //   }
  // })();
  const storageData = getLocalStorageData(product);

  const formData = new FormData();

  const message = values.message?.trim() ?? "";

  const finalFields: Record<string, any> = {
    requestedPDF,
    ...(IS_TEST_LEAD && {
      Test_Lead: true,
      Lead_Source: "Test_source",
    }),
    ...dataFromForm,

    Note_Title: "Configurator Note",
    Note_Content: message,

    // explicitly sent, but intentionally empty
    Configurator_message_from_lead: "",

    pdf_extra_data: JSON.stringify(storageData),
  };

  Object.entries(finalFields).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    formData.append(k, String(v));
  });

  const heroBytes: Uint8Array | null =
    (imagesData as any).heroImageBytes ?? null;
  const setBytes: Uint8Array[] = (imagesData as any).imagesSetBytes ?? [];
  const textures: TextureUrls = (imagesData as any).textures ?? {};

  const imageFormDataKey = "images";
  const appendBytesAsImage = (bytes: Uint8Array, index: number) => {
    const blob = new Blob([bytes], { type: "image/jpeg" });
    formData.append(imageFormDataKey, blob, `image-${index}.jpg`);
  };

  /* ORDER 1 (don't change the order, it matters) */
  let idx = 0;
  if (heroBytes?.byteLength) appendBytesAsImage(heroBytes, idx++);

  for (const b of setBytes) {
    if (idx >= 8) break;
    if (b?.byteLength) appendBytesAsImage(b, idx++);
  }

  /* ORDER 2 (don't change the order, it matters) */
  if (Object.values(textures)?.length) {
    await appendTextureUrlsToFormData(formData, textures, {
      formDataKey: imageFormDataKey,
    });
  }

  if (IS_DEVELOPMENT) {
    // @ts-ignore
    for (const [k, v] of formData.entries()) {
      if (v instanceof File) console.log(k, "FILE", v.name, v.size);
      else console.log(k, "FIELD bytes", new Blob([String(v)]).size);
    }
  }

  try {
    const res = await createLead(formData);
    if (IS_DEVELOPMENT) {
      console.log("res", res);
    }
  } catch (e) {
    console.log("Something went wrong", e);
  }
}

const categoriesToEmit = [
  MainCategoriesEnum.HULL,
  MainCategoriesEnum.FLOORING,
  MainCategoriesEnum.FABRICS,
];
const checkShouldEmit = (cat: MainCategoriesEnum): boolean =>
  categoriesToEmit.includes(cat);

const getMainCategoryKey = (main: string, sub: string): string => {
  if (EMIT_NESTED_CATEGORIES.includes(sub)) {
    main += `_${sub}`;
  }
  return main;
};

const getPropulsionEmit = (product: ProductNameEnum, uid?: string | null) => {
  const emitId = getMainCategoryInfo(
    product,
    MainCategoriesEnum.PROPULSION,
  )?.items.find((el: any) => el.uid === uid)?.emitId;
  return emitId ? { [MainCategoriesEnum.PROPULSION]: emitId } : null;
};

const emulateResize = (() => {
  let timeout: number | null = null;

  return (delay = 50) => {
    if (timeout) return;

    timeout = window.setTimeout(() => {
      //window.dispatchEvent(new Event("resize"));
      timeout = null;
    }, delay);
  };
})();

function App(): any {
  const [openDrawer, setOpenDrawer] =
    useState(DISABLE_STREAM); /* @TODO hardcode */
  const [drawerHeight, setDrawerHeight] = useState(56);

  /*useEffect(() => {
    const timeoutId = emulateResize(1000);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [drawerHeight]);*/

  const loaderSimulationRef = useRef<ReturnType<
    typeof createLoaderSimulator
  > | null>(null);
  const staticHeightRef = useRef(null);
  const heroImageBytesRef = useRef(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [showLoader, setShowLoader] = useState<boolean>(!DISABLE_STREAM);

  // const [thankTo, setThankTo] = useState<{ name?: string; email?: string; pdf?: boolean }>({
  //   name: 'Max',
  //   email: 'maxspas1@gmail.com',
  //   pdf: false,
  // });
  const [thankTo, setThankTo] = useState<{
    name?: string;
    email?: string;
    pdf?: boolean;
  }>({});
  const [sendingLeadData, setSendingLeadData] = useState<boolean>(false);
  // const [sendingLeadData, setSendingLeadData] = useState<boolean>(false);
  const [fetchingModel, setFetchingModel] = useState<boolean>(false);
  const hasThankTo = !!Object.values(thankTo)?.length;

  const thanksText = useMemo(() => {
    if (hasThankTo) {
      return thankTo?.pdf
        ? {
            title: `Thank you${thankTo?.name ? " " + thankTo.name : ""}!`,
            description: `“Your SAY Configuration PDF has been sent to ${thankTo.email}.”`,
          }
        : {
            title: "Thank you for your request!",
            description:
              "Our sales team will contact you shortly to discuss your personalized configuration options.",
          };
    }
    return {};
  }, [hasThankTo, thankTo]);

  // const [loading, setLoading] = useState<boolean>(true);
  // const streamRef = useRef<ArcwareApplication | null>(null);
  // const [connected, setConnected] = useState<boolean>(false);

  const [showChangeModelDialog, setShowChangeModelDialog] =
    useState<boolean>(false);
  const [showContactForm, setShowContactForm] = useState<boolean>(false);
  const [showPdfForm, setShowPdfForm] = useState<boolean>(false);
  const [formBanner, setFormBanner] = useState<string | undefined>(undefined);

  const { isMobileDevice } = useDeviceStyles();
  const activeProduct = useActiveProduct();
  const activeCategory = useActiveCategory();
  const catalog = useActiveCatalog();
  const currentSelection = useCurrentSelection();
  const selection = useSelection(activeCategory);
  const selectedGroup = useSelectedGroup(activeCategory);
  const groupBuckets = useGroupBuckets(activeCategory);
  const activeBucket = useActiveBucket(activeCategory);
  const { getPriceFinal } = useFinalItemPrice();
  const { rpx } = useResponsiveScaler();
  // console.log('catalog', catalog);
  const setActiveProduct = useConfigStore((s) => s.setActiveProduct);
  const configData = useConfigStore((s) => s.products[activeProduct]);
  const activeMode = useConfigStore(
    (s) => s.products[s.activeProduct]?.ui.mode,
  );
  const activeLed = useConfigStore((s) => s.products[s.activeProduct]?.ui.led);
  const resetAll = useConfigStore((s) => s.resetAllProductsToDefaults);

  const shouldEmitData = checkShouldEmit(activeCategory);

  const currentDisplaySelection = useMemo(():
    | {
        name: string;
        displayDeltaPrice?: string;
      }
    | {} => {
    if (!selection) return {};
    const currentId =
      selection.kind === "multi"
        ? selection.uids[selection.uids.length - 1]
        : selection.uid;
    const needle = activeBucket.items.find((item) => item.uid === currentId);
    // console.log('needle', needle);
    if (!needle) return {};
    const displayDeltaPrice = getPriceFinal(needle?.price);
    return {
      name: needle?.name,
      ...(displayDeltaPrice && { displayDeltaPrice }),
    };
  }, [activeBucket.items, getPriceFinal, selection]);

  const currentDisplaySelectionData = useMemo((): any[] => {
    const entries = Object.entries(currentDisplaySelection);
    if (!entries.length) return [];

    return entries.reduce((acc, [k, v]) => {
      /* @ts-ignore */
      acc.push({
        text: v,
        active: k === "name",
      });
      return acc;
    }, []);
  }, [currentDisplaySelection]);

  /* Might be necessary later */
  // const {
  //   maxWidth: containerMaxWidth,
  //   height: containerHeight
  // } = useStreamContainerSize({
  //   aspectRatio: 16 / 9,
  //   maxContainerWidth: 980,
  //   gutterHeight: 100,
  //   debounceDelay: 15,
  //   minHeight: 400,
  //   autoAdjust: [{ max: theme.breakpoints.values["md"] }]
  // });
  const modelRetryRef = useRef<number | null>(null);
  const modelAckedRef = useRef<string | null>(null);
  const settleGateRef = useRef<StreamSettleGate | null>(null);
  const hardTimeoutRef = useRef<number | null>(null);

  const configRef = useRef(configData);
  // onAppResponse below is a first-render closure — it must read the CURRENT
  // product through a ref to recognize stale echoes after a model switch.
  const activeProductRef = useRef(activeProduct);

  useEffect(() => {
    configRef.current = configData;
    activeProductRef.current = activeProduct;
  }, [configData, activeProduct]);

  const { streamRef, mount, connected, emitUIInteraction } = useArcwareStream({
    containerId: STREAM_ID,
    shareId: ARCWARE_SHARE_ID,
    settings: {
      // startWidth: containerMaxWidth,
      // startHeight: containerHeight,
    },
    onVideoInitialized: () => {
      if (loaderTimeoutRef.current) {
        window.clearTimeout(loaderTimeoutRef.current);
        loaderTimeoutRef.current = null;
      }
      setTimeout(() => {
        loaderSimulationRef.current?.resume();
      }, 2500);
    },
    onAppResponse: (res, api) => {
      let parsed;

      try {
        parsed = typeof res === "string" ? JSON.parse(res) : res;
      } catch {
        return;
      }

      if (parsed?.change_model) {
        console.log("Model switch response from Unreal:", parsed.change_model);

        // A late echo for a previously requested model must not kill the
        // retry/timeout of the CURRENT load — ignore it and let the loop
        // keep re-requesting the right one.
        if (parsed.change_model !== activeProductRef.current) return;

        // Unreal confirmed the model is loaded — stop the retry loop and the
        // hard timeout (otherwise its warning fires even after a late ack).
        modelAckedRef.current = parsed.change_model;
        if (modelRetryRef.current) {
          window.clearInterval(modelRetryRef.current);
          modelRetryRef.current = null;
        }
        if (hardTimeoutRef.current) {
          window.clearTimeout(hardTimeoutRef.current);
          hardTimeoutRef.current = null;
        }

        // Sync the config first so hull/floor/fabric repaints happen behind
        // the loader. The echo only means the blueprint spawned — textures,
        // LODs and shaders still stream in visibly after it, so keep the
        // loader up until the video has played hitch-free for a moment.
        // Re-raising fetchingModel is a no-op for normal acks but pulls the
        // loader back up when the ack arrives after the hard-timeout reveal.
        setFetchingModel(true);
        const data = syncInterfaceWithStream(configRef.current);
        data.forEach((entry) => emitUIInteraction(entry));
        setFloatingPointsVisible(true);

        settleGateRef.current?.cancel();
        settleGateRef.current = waitForStreamSettle(
          getStreamVideoElement(streamRef.current),
        );
        settleGateRef.current.promise.then((settled) => {
          if (settled) setFetchingModel(false);
        });
      }
    },
  });

  const setFloatingPointsVisible = useCallback(
    (visible: boolean) => {
      emitUIInteraction({
        fp: visible ? "fp_visible" : "fp_invisible",
      });
    },
    [emitUIInteraction],
  );

  const drawerReady = connected && !showLoader && !fetchingModel;

  const tRef = useRef<number | null>(null);

  const streamContainerRef = useRef<HTMLDivElement | null>(null);

  /* Fix "stream black edges" bug */
  /*useResizeObserverRaf(streamContainerRef as any, ({ width, height }) => {
    if (tRef.current) window.clearTimeout(tRef.current);
    tRef.current = emulateResize(50);
  });*/

  // const { isXXLarge, isXXXLarge } = useSizes();
  const mainCategoryStaticData = useMainCategoryStaticData();
  const orientation = useOrientation();
  // const { isMobile: isMobileDevice } = useResponsiveDevice({
  //   breakpoint_mobile: theme.breakpoints.values[BREAKPOINT_MOBILE]
  // });
  // console.log('isMobileDevice', isMobileDevice);
  const totalPrice = useTotalPrice(activeProduct, {
    basePrice: MODELS_DATA[activeProduct].basePrice,
  });

  const { width: debugWidth, breakpoint: debugBreakpoint } = useDebugData({
    applyGlobalStyles: true,
  });

  // rotate overlay disabled (portrait no longer forced on mobile)
  const shouldRotateMobileDevice = isMobileDevice && orientation === "portrait";

  // Safari 26 Liquid Glass: in landscape, let a swipe collapse Safari's bars
  const { active: iosLandscapeChrome, chromeCollapsed } =
    useIosLandscapeChrome({
      enabled: isMobileDevice && orientation === "landscape",
    });

  // Android: real Fullscreen API — go immersive on the first tap/scroll.
  useAndroidFullscreen({
    enabled: isMobileDevice,
  });

  const selectionUid = useMemo(() => {
    if (!selection) return null;
    if (selection.kind === "single") return selection.uid;
    if (!selection.uids.length) return null;
    return selection.uids[selection.uids.length - 1];
  }, [selection]);

  const { activeIndex, setIndexForGroup, getIndexForGroup } =
    useLocalPerGroupIndex({
      product: activeProduct,
      category: activeCategory,
      groupBuckets,
      activeBucket,
      selectionUid,
    });

  type FrameBytes = Awaited<ReturnType<typeof captureVideoFrameBytes>>;

  const captureStream = useCallback(async (): Promise<FrameBytes> => {
    const videoTag = getStreamVideoElement(streamRef.current);
    if (!videoTag) return null as FrameBytes;

    // Optional: wait until the video has data
    if (videoTag.readyState < 2) {
      await new Promise<void>((resolve) => {
        const onLoaded = () => {
          videoTag.removeEventListener("loadeddata", onLoaded);
          resolve();
        };
        videoTag.addEventListener("loadeddata", onLoaded, { once: true });
        // Fallback: don't hang forever
        setTimeout(resolve, 500);
      });
    }

    return await captureVideoFrameBytes(videoTag, "image/jpeg", 0.85, 1920);
  }, [streamRef]);

  useEffect(() => {
    let timeout: number;
    let isInternal = false;

    const handleResize = () => {
      if (isInternal) return;

      clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        isInternal = true;
        window.dispatchEvent(new Event("resize"));
        setTimeout(() => {
          isInternal = false;
        }, 0);
      }, 120);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const shouldCapture = showPdfForm || showContactForm;
      if (!shouldCapture) return;

      // Try a few times because the video element may not be armed immediately
      for (let attempt = 0; attempt < 5; attempt++) {
        if (cancelled) return;

        const bytes = await captureStream();
        if (bytes && (bytes as any).byteLength > 0) {
          /* @ts-ignore */
          heroImageBytesRef.current = bytes;
          return;
        }

        await sleep(200); // small backoff
      }

      // If still nothing, keep null and let the submit handle "no image"
      heroImageBytesRef.current = null as any;
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [showPdfForm, showContactForm, captureStream]);

  const loaderTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    localStorage.removeItem("pxss");

    if (!connected) return;

    emitUIInteraction({
      fp: "fp_visible",
    });

    loaderSimulationRef.current = createLoaderSimulator({
      onProgress: setLoadingProgress,
      onDone: () =>
        setTimeout(() => {
          setShowLoader(false);
        }, 800),
      pauseAt: 1,
      durationToPauseMs: 1000,
      durationToEndMs: 800,
      easing: (t) => t,
    });

    loaderSimulationRef.current.start();

    loaderTimeoutRef.current = window.setTimeout(() => {
      console.warn("Loader fallback triggered");

      loaderSimulationRef.current?.resume(); // finish animation
      setShowLoader(false); // ensure it's gone
    }, 10000); // e.g. 10s max

    return () => {
      loaderSimulationRef.current?.cancel();
      if (loaderTimeoutRef.current) {
        window.clearTimeout(loaderTimeoutRef.current);
      }
    };
  }, [connected]);

  useEffect(() => {
    if (shouldRotateMobileDevice) {
      setOpenDrawer(false);
    }
  }, [shouldRotateMobileDevice]);

  // console.log('activeProduct', activeProduct);
  // console.log('activeCategory', activeCategory);
  // console.log("catalog", catalog);
  // console.log("selectedGroup", selectedGroup);
  // console.log("configData", configData);
  // console.log("activeProduct", activeProduct);
  // console.log('currentSelection', currentSelection);
  // console.log('selection | activeIndex', selection, activeIndex);
  // console.log('selectedGroup', selectedGroup);
  // console.log('groupBuckets', groupBuckets);
  // console.log("selection", selection);
  // console.log("activeBucket", activeBucket);
  // console.log('activeProduct', activeProduct);
  // console.log('activeCategory', activeCategory);
  // console.log('currentSelection', currentSelection);
  // console.log("==============================");

  const syncInterfaceWithStream = useCallback(
    (productConfig: any): [] => {
      if (!productConfig?.config || !productConfig?.ui?.product) return [];
      console.log("syncInterfaceWithStream");

      const emitCategories = Object.entries(productConfig.config).reduce(
        (acc: any[], [key, value]: [string, any]) => {
          if (checkShouldEmit(key as MainCategoriesEnum)) {
            /* @ts-ignore */
            const group = catalog[key].items.find(
              (el: any) => el.uid === value.uid,
            ).group;
            const emitKey = getMainCategoryKey(key, group);
            /* @ts-ignore */
            const emitId = value?.uid as any;
            const emitCategory = { [emitKey]: emitId };

            /* @ts-ignore */
            acc.push(emitCategory);
          }
          return acc;
        },
        [],
      );

      const propulsion = getPropulsionEmit(
        productConfig.ui.product,
        productConfig.config[MainCategoriesEnum.PROPULSION]?.uid,
      );

      const emitData = [
        { activeProduct: productConfig.ui.product },
        { daytime: productConfig.ui.mode },
        { led_colors: productConfig.ui.led },
        ...(propulsion ? [propulsion] : []),
        ...emitCategories,
      ];

      /* @ts-ignore */
      return emitData;
    },
    [catalog],
  );

  /* Sync pathname and state */
  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    const pathname = location?.pathname;
    if (!pathname) return;

    const commandsEntries = Object.entries(TRICKY_COMMANDS);
    for (let i = 0; i < commandsEntries.length; i++) {
      const [key, value] = commandsEntries[i];
      if (pathname === value.pathName) {
        setActiveProduct(key as ProductNameEnum);
        break;
      }
    }
  }, []);

  useEffect(() => {
    if (shouldEmitData) {
      emitUIInteraction({
        camera_view: activeCategory,
      });
    }
  }, [activeCategory]);

  useEffect(() => {
    // A live connection is required to actually deliver the model command.
    // Gating on `connected` (not just `mount`) also means this re-runs after an
    // AFK reconnect, so the yacht is re-requested on the fresh stream.
    if (!mount || !connected) return;

    setFetchingModel(true);
    setFloatingPointsVisible(false);

    /* @TODO Removed sync-feature */
    resetAll({ resetActiveInMemory: true });

    // change_model is fire-and-forget: if it reaches Unreal before its
    // blueprint is ready (or the data channel isn't open yet) it is silently
    // dropped — the stream renders the water/sky but the yacht never spawns.
    // Re-emit until Unreal echoes the change_model response (handled in
    // onAppResponse), which is the only signal that the model actually loaded.
    // The loader (fetchingModel) stays up for the whole wait, so the user never
    // sees the empty water-without-yacht frame between reconnect and spawn.
    modelAckedRef.current = null;

    // A settle gate from a previous ack must not reveal the new load early.
    settleGateRef.current?.cancel();
    settleGateRef.current = null;

    const clearRetry = () => {
      if (modelRetryRef.current) {
        window.clearInterval(modelRetryRef.current);
        modelRetryRef.current = null;
      }
    };

    let attempts = 0;
    const tryEmit = () => {
      if (
        modelAckedRef.current === activeProduct ||
        ++attempts > MODEL_LOAD_MAX_ATTEMPTS
      ) {
        clearRetry();
        return;
      }
      emitUIInteraction({
        change_model: activeProduct,
        // Only snap the camera on the first attempt — a retry against an
        // alive-but-not-echoing instance must not keep yanking the view.
        ...(attempts === 1 && { camera_view: "reset_camera" }),
      });
    };

    tryEmit(); // immediate first attempt
    modelRetryRef.current = window.setInterval(tryEmit, MODEL_LOAD_RETRY_MS);

    // Safety net so a dead Unreal instance can't trap the user behind an
    // infinite loader. Reveals the stream but deliberately KEEPS the retry
    // loop running — if the ack still arrives later (slow instance boot after
    // a reconnect), the yacht appears late instead of never.
    hardTimeoutRef.current = window.setTimeout(() => {
      console.warn(
        "change_model: no ack within hard timeout, revealing stream",
      );
      setFetchingModel(false);
      setFloatingPointsVisible(true);
    }, MODEL_LOAD_HARD_TIMEOUT_MS);

    return () => {
      clearRetry();
      if (hardTimeoutRef.current) {
        window.clearTimeout(hardTimeoutRef.current);
        hardTimeoutRef.current = null;
      }
      settleGateRef.current?.cancel();
    };
  }, [activeProduct, mount, connected, emitUIInteraction]);

  useEffect(() => {
    if (!mount || !connected) return;

    scrollToElement(`#root`);
  }, [connected, mount]);

  useEffect(() => {
    if (!mount || !connected) return;
    emitUIInteraction({
      daytime: activeMode,
    });
  }, [activeMode, mount, connected, emitUIInteraction]);

  useEffect(() => {
    if (!mount || !connected) return;
    emitUIInteraction({
      led_colors: activeLed,
    });
  }, [activeLed, mount, connected, emitUIInteraction]);

  const prevSelectionRef = useRef<string | null>(null);

  useEffect(() => {
    if (!mount) return;
    if (
      selectedGroup &&
      shouldEmitData &&
      currentSelection?.kind === "single"
    ) {
      const currentUid = currentSelection.uid;

      if (prevSelectionRef.current === currentUid) return;
      prevSelectionRef.current = currentUid;

      const key = getMainCategoryKey(activeCategory as string, selectedGroup);

      emitUIInteraction({
        [key]: currentUid,
      });
    }
  }, [activeCategory, currentSelection, selectedGroup, shouldEmitData, mount]);

  const prevExtrasRef = useRef<string[] | null>(null);

  const extrasUids = useMemo<string[]>(() => {
    const extras = configData?.config?.[MainCategoriesEnum.EXTRAS];
    if (!extras || extras.kind !== "multi") return [];
    return extras?.uids ?? [];
  }, [configData?.config?.[MainCategoriesEnum.EXTRAS]]);

  useEffect(() => {
    if (!mount) return;

    const current = extrasUids;
    const prev = prevExtrasRef.current;

    if (!prev) {
      prevExtrasRef.current = current;
      return;
    }

    const added = current.filter((uid) => !prev.includes(uid));
    const removed = prev.filter((uid) => !current.includes(uid));

    added.forEach((uid) => {
      emitUIInteraction({ extras: `${uid}true` });
    });

    removed.forEach((uid) => {
      emitUIInteraction({ extras: `${uid}false` });
    });

    prevExtrasRef.current = current;
  }, [extrasUids, mount]);

  const propulsion = configData?.config?.[MainCategoriesEnum.PROPULSION];
  const propulsionUid = propulsion?.kind === "single" ? propulsion.uid : null;
  const prevPropulsionRef = useRef({
    product: activeProduct,
    uid: propulsionUid,
  });

  useEffect(() => {
    if (!mount) return;

    const prev = prevPropulsionRef.current;
    prevPropulsionRef.current = { product: activeProduct, uid: propulsionUid };
    if (prev.product !== activeProduct || prev.uid === propulsionUid) return;

    const entry = getPropulsionEmit(activeProduct, propulsionUid);
    if (entry) emitUIInteraction(entry);
  }, [activeProduct, propulsionUid, mount, emitUIInteraction]);

  /* @TODO Removed sync-feature */
  // useEffect(() => {
  //   if (!mount) return;
  //   syncInterfaceWithStream(configData);
  // }, [activeProduct, configData, syncInterfaceWithStream]);

  const leftRef = React.useRef<HTMLDivElement | null>(null);
  const rightRef = React.useRef<HTMLDivElement | null>(null);
  const [sideWidth, setSideWidth] = React.useState<number | null>(null);
  useEffect(() => {
    if (!leftRef.current || !rightRef.current) return;

    let frame: number | null = null;

    const measure = () => {
      if (frame) cancelAnimationFrame(frame);

      frame = requestAnimationFrame(() => {
        const left = Math.max(
          leftRef.current!.scrollWidth,
          leftRef.current!.getBoundingClientRect().width,
        );
        const right = Math.max(
          rightRef.current!.scrollWidth,
          rightRef.current!.getBoundingClientRect().width,
        );
        const next = Math.ceil(Math.max(left, right));

        setSideWidth((prev) => (prev === next ? prev : next));
      });
    };

    measure();

    const fonts = (document as any).fonts;
    if (fonts?.ready) {
      fonts.ready.then(measure).catch(() => {});
    }

    const ro = new ResizeObserver(measure);
    ro.observe(leftRef.current!);
    ro.observe(rightRef.current!);

    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    emulateResize(100);
  }, [drawerHeight]);

  const gatherImagesForPDF = useCallback(async () => {
    setFloatingPointsVisible(false);

    const data = {
      heroImageBytes: heroImageBytesRef.current,
      imagesSetBytes: [] as any[],
      textures: getSelectedImages(activeProduct, categoriesToEmit),
    };

    const trickyCommands = TRICKY_COMMANDS[activeProduct]?.camera_view ?? [];
    // Hero (index 0) uses a dedicated PDF camera when defined, otherwise the
    // regular first view — keeps the in-form preview (camera_view[0]) untouched
    // and stays a no-op for products without pdf_hero_camera.
    const pdfHeroCamera = TRICKY_COMMANDS[activeProduct]?.pdf_hero_camera;

    try {
      for (let i = 0; i < trickyCommands.length; i++) {
        const command =
          i === 0 ? pdfHeroCamera ?? trickyCommands[0] : trickyCommands[i];

        emitUIInteraction({ camera_view: command });
        // Re-hide floating points before every screenshot — switching
        // camera_view can bring them back, so re-issue fp_invisible each frame.
        setFloatingPointsVisible(false);

        await sleep(TIMEOUT_SCREENSHOTS);

        const screen = await captureStream();

        if (screen) {
          if (i === 0) {
            /* @ts-ignore - Hero banner on '0' index */
            data.heroImageBytes = screen;
          } else {
            data.imagesSetBytes.push(screen);
          }
        }
      }
    } finally {
      setFloatingPointsVisible(true);

      emitUIInteraction({
        camera_view: "reset_camera",
      });
    }

    return data;
  }, [activeProduct, emitUIInteraction, captureStream]);

  const handleSubmit = useCallback(
    async (values: FormValues, requestedPDF: boolean) => {
      if (sendingLeadData) return;

      console.log("values", values, requestedPDF);

      setSendingLeadData(true);
      setThankTo({
        name: values["first_name"],
        email: values["email"],
        pdf: requestedPDF,
      });

      let imagesData = {};
      if (requestedPDF) {
        imagesData = await gatherImagesForPDF();
      }

      try {
        await onSubmitLead(values, {
          product: activeProduct,
          requestedPDF,
          imagesData,
        });
      } catch (err) {
        alert("Something went wrong, please try again later");
      }
      /* Temp commented (fp_visible | fp_invisible) */
      /*emitUIInteraction({
        fp: "fp_visible",
      });*/
      setSendingLeadData(false);

      /* Hide all forms */
      setShowContactForm(false);
      setShowPdfForm(false);

      setTimeout(() => {
        setThankTo({});
      }, TIMEOUT_FORM_SUBMIT_OVERLAY);
    },
    [activeProduct, emitUIInteraction, gatherImagesForPDF, sendingLeadData],
  );

  const ContactUsLinkMemo = useMemo(
    () => (
      <BrandLink
        onClick={(e) => {
          e.stopPropagation();
          setShowContactForm(true);
        }}
        icon={isMobileDevice ? undefined : IconArrowLeft}
        sx={(theme) => ({
          justifySelf: "start",
          whiteSpace: "pre",
          marginBottom: "5px",
          [theme.breakpoints.up(BREAKPOINT_MOBILE)]: {
            marginBottom: "16px",
          },
          [theme.breakpoints.down(BREAKPOINT_MOBILE)]: {
            justifyContent: "flex-start",
          },
        })}
      >
        Contact us
      </BrandLink>
    ),
    [],
  );

  const ReceivePDFMemo = useMemo(
    () => (
      <BrandLink
        onClick={() => {
          if (streamRef.current) {
            /* @TODO should check disconnected state */

            /* Main banner with no dots in the form */
            const mainBannerCommand =
              TRICKY_COMMANDS[activeProduct].camera_view[0] ?? [];
            emitUIInteraction({
              camera_view: mainBannerCommand,
            });

            setFloatingPointsVisible(false);

            setTimeout(() => {
              const videoTag = getStreamVideoElement(streamRef.current);
              const banner = videoTag ? captureVideoFrame(videoTag) : null;
              if (banner) {
                setFormBanner(banner);
              }

              setFloatingPointsVisible(true);

              emitUIInteraction({
                camera_view: "reset_camera",
              });
            }, TIMEOUT_SCREENSHOTS);
          }
          setShowPdfForm(true);
        }}
        icon={isMobileDevice ? undefined : IconArrowRight}
        iconPosition={"right"}
        sx={(theme) => ({
          justifySelf: "end",
          whiteSpace: "pre",
          marginBottom: "5px",
          [theme.breakpoints.up(BREAKPOINT_MOBILE)]: {
            marginBottom: "16px",
          },
          [theme.breakpoints.down(BREAKPOINT_MOBILE)]: {
            justifyContent: "flex-end",
          },
        })}
      >
        Receive as PDF
      </BrandLink>
    ),
    [isMobileDevice, streamRef, activeProduct, emitUIInteraction],
  );

  const PriceBlockMemo = useMemo(() => {
    return (
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          textAlign: "right",
          marginTop: {
            [BREAKPOINT_MOBILE]: "38px",
            xxxl: "74px",
          },
          // marginBottom: !isMobileDevice ? "38px" : 0,
          marginBottom: {
            [BREAKPOINT_MOBILE]: "60px",
            xxxl: "74px",
          },
        })}
      >
        <Typography
          sx={{ lineHeight: 1.3, color: colors.text.v4 }}
          variant={"v3"}
        >
          {getPriceWithCurrency(totalPrice)}
        </Typography>
        <Typography
          sx={{
            lineHeight: 1.3,
            ...deviceStyles({ mobile: { fontSize: "10px" } }),
          }}
          variant={"v2_special"}
        >
          VAT&nbsp;
          {VAT > 1 ? "included" : "excluded"}
        </Typography>
      </Box>
    );
  }, [totalPrice]);

  return (
    <StyledApp className="App">
      <Loader
        hideLoadingText={true}
        open={Boolean(hasThankTo || sendingLeadData)}
        logoProps={{
          progress: 1,
        }}
        sx={{
          ".animated-logo": {
            display: sendingLeadData ? undefined : "none !important",
          },
        }}
      >
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          {hasThankTo && !sendingLeadData && (
            <IconCheckMark
              sx={(theme) => ({
                color: colors.text.v4,
                margin: "30px auto",
                width: "121px",
                height: "121px",
                [theme.breakpoints.down("xl")]: {
                  width: "50px",
                  height: "50px",
                },
              })}
            />
          )}

          {sendingLeadData && (
            <Typography
              variant={"v6"}
              sx={{
                color: colors.text.v4,
                marginTop: "24px",
              }}
            >
              {thankTo?.pdf
                ? "Generating your configuration"
                : "Sending request"}
              &nbsp;...
            </Typography>
          )}

          {!sendingLeadData && (
            <>
              {thanksText?.title && (
                <Typography
                  variant={"v6"}
                  sx={{
                    color: colors.text.v4,
                    marginBottom: "24px",
                  }}
                >
                  {thanksText?.title}
                </Typography>
              )}

              {thanksText?.description && (
                <Typography
                  variant={"v1"}
                  sx={(theme) => ({
                    ...convertToBreakpoints(theme, {
                      xs: { marginTop: "10px" },
                      md: { marginTop: "3vh" },
                    }),
                  })}
                >
                  {thanksText?.description}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Loader>

      <Loader
        sx={{
          /* @TODO hardcode */
          // display: 'none',
          ...(shouldRotateMobileDevice && {
            paddingBottom: "130px",
          }),
        }}
        open={showLoader || shouldRotateMobileDevice || fetchingModel}
        logoProps={{
          progress: loadingProgress,
        }}
        // @TODO hardcode
        display={DISABLE_STREAM ? "none" : undefined}
      >
        {shouldRotateMobileDevice && (
          <Box
            sx={(theme) => ({
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              // Pin to the full screen, independent of the loader column's flow
              // (otherwise the animating "loading" dots could nudge its X).
              position: "fixed",
              left: 0,
              right: 0,
              bottom: "100px",
              // [theme.breakpoints.down(BREAKPOINT_MOBILE)]: {}
            })}
          >
            <RotatingBox>
              <StyledIconRotate />
            </RotatingBox>
            <Typography
              variant={"v1"}
              sx={(theme) => ({
                marginTop: "50px",
                // ...convertToBreakpoints(theme, {
                //   xs: { marginTop: "10px" },
                //   md: { marginTop: "3vh" }
                // })
              })}
            >
              Please rotate your device
            </Typography>
          </Box>
        )}
      </Loader>

      {window.location.href.includes("localhost") && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            color: colors.text.v4,
            zIndex: 999,
          }}
        >
          {debugBreakpoint} : {debugWidth}
        </Box>
      )}

      <Header
        onChangeModel={(uid) => {
          // setShowChangeModelDialog(true)
          setShowChangeModelDialog(false);
          setActiveProduct(uid);
        }}
      />

      <StyledVideoWrapper
        className={"fullscreen-area"}
        sx={{
          minWidth: 0,
          minHeight: 0,
          ...(isMobileDevice && {
            height: isMobileDevice ? `calc(100% - ${drawerHeight}px)` : "100%",
            transition: "height 0.3s ease",
          }),
          // maxWidth: containerMaxWidth,
          // height: containerHeight,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            padding: "16px",
            boxSizing: "border-box",

            "& video": {
              width: "100% !important",
              height: "100% !important",
              objectFit: "cover !important",
            },
          }}
        >
          <Box
            ref={streamContainerRef}
            id={STREAM_ID}
            sx={{
              width: "100%",
              height: "100%",
              overflow: "hidden",
              // While bars are visible, pass vertical swipes through as native scroll so they reach Safari
              ...(iosLandscapeChrome &&
                !chromeCollapsed && {
                  touchAction: "pan-y",
                  "& *": { touchAction: "pan-y !important" },
                }),
            }}
          />
        </Box>
        {!isMobileDevice && (
          <>
            <FullscreenGlobalStyles />
            <FullscreenController
              selector=".fullscreen-area"
              enterClassName={"no-padding"}
            >
              {({ isSupported, isFullscreen, enter, exit }) =>
                isSupported && (
                  <Box
                    sx={(theme) => ({
                      position: "absolute",
                      right: "25px",
                      bottom: "25px",
                      color: colors.icons.v1,
                      cursor: "pointer",
                      zIndex: 99,
                      opacity: 1,
                      transition: transitions.join(["color"]),
                      ":hover": {
                        color: "#fff",
                      },
                    })}
                  >
                    {isFullscreen ? (
                      <IconZoomOut
                        onClick={() => {
                          void exit();
                          emulateResize(50);
                        }}
                      />
                    ) : (
                      <IconZoomIn
                        onClick={() => {
                          void enter();
                          emulateResize(50);
                        }}
                      />
                    )}
                  </Box>
                )
              }
            </FullscreenController>
          </>
        )}
      </StyledVideoWrapper>

      <ConditionalWrapper
        condition={isMobileDevice && !shouldRotateMobileDevice}
        mainWrapper={
          <BottomDrawer
            hidden={!drawerReady || sendingLeadData || hasThankTo}
            open={openDrawer}
            onOpenChange={setOpenDrawer}
            onToggle={({ height }) => setDrawerHeight(height)}
            // peekHeight={openDrawer ? DRAWER_PICK_HEIGHT_COLLAPSED : DRAWER_PICK_AREA_HEIGHT}
            peekHeight={
              openDrawer
                ? DRAWER_PICK_HEIGHT_COLLAPSED
                : DRAWER_PICK_AREA_HEIGHT
            }
            childrenCollapsed={
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  padding: "0 32px",
                  justifyContent: "space-between",
                  position: "absolute",
                  transition: transitions.join(["opacity"]),
                  opacity: openDrawer ? 0 : 1,
                  userSelect: openDrawer ? "none" : "unset",
                  minHeight: 0,
                }}
              >
                {ContactUsLinkMemo}
                {PriceBlockMemo}
              </Box>
            }
          />
        }
      >
        <Box
          ref={staticHeightRef}
          sx={(theme) => ({
            position: "relative",
            overflow: isMobileDevice ? "visible" : "unset",

            ...(isMobileDevice && {
              display: "flex",
              flexDirection: "column",
              height: "100%", // ✅ IMPORTANT
              minHeight: 0,
            }),
          })}
        >
          <Box
            sx={{
              height: isMobileDevice ? 27 : 55,
              minHeight: isMobileDevice ? 27 : 55,
              maxHeight: isMobileDevice ? 27 : 55,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box",
            }}
          >
            <MainCategories
              onModelClick={() => {
                emitUIInteraction({
                  camera_view: "reset_camera",
                });
              }}
            />
          </Box>

          <Box
            sx={(theme) => ({
              ...deviceStyles({
                mobile: {
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                  padding: "0 32px",
                },
              }),
            })}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: isMobileDevice ? 0 : 34,
              }}
            >
              {shouldEmitData && !isMobileDevice && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                    justifyContent: "center",
                  }}
                >
                  <GroupedCategories
                    getIndexForGroup={getIndexForGroup}
                    orderCarouseProps={{
                      itemClassName: "item",
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Textures/Extra list | START */}
            <Box
              sx={(theme) => ({
                display: "grid",
                gridTemplateColumns: {
                  xs: sideWidth
                    ? `${sideWidth}px minmax(0, 1fr) ${sideWidth}px`
                    : "auto minmax(0, 1fr) auto",

                  justifyContent: "space-between",
                },

                // ✅ KEY FIX (mobile only)
                alignItems: isMobileDevice ? "stretch" : "center",
                ...(isMobileDevice && {
                  height: "100%",
                }),

                width: "100%",
                color: colors.text.v4,
              })}
            >
              {
                <Box
                  ref={leftRef}
                  sx={{
                    minWidth: 0, // ✅ CRITICAL
                    width: "auto",
                    display: "flex",
                    flexDirection: "column",
                    alignSelf: isMobileDevice ? "flex-end" : "center",
                    justifyContent: isMobileDevice ? "center" : "unset",
                    paddingTop: isMobileDevice ? "5px" : 0,
                    height: "100%",
                  }}
                >
                  {isMobileDevice && (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          minWidth: 0,
                          ...deviceStyles({
                            mobile: {
                              marginTop: "auto",
                              marginBottom: "auto",
                            },
                          }),
                        }}
                      >
                        {shouldEmitData && (
                          <>
                            <GroupedCategories
                              getIndexForGroup={getIndexForGroup}
                              sx={{
                                padding: "0 !important",
                                lineHeight: "1 !important",
                                "*": {
                                  lineHeight: "1 !important",
                                  width: "auto !important",
                                },
                              }}
                              orderCarouseProps={{
                                shift: false,
                                sx: {
                                  flexDirection: "column-reverse",
                                  display: "flex",
                                  textAlign: "left",
                                  gap: "4px", // ✅ spacing WITHOUT layout shift

                                  "& .MuiTypography-root": {
                                    lineHeight: 1.3,
                                    fontSize: "12px",
                                  },
                                },
                              }}
                            />
                            <IconArrowSharpRight
                              sx={{
                                width: "10.5px", // 7 * 1.5
                                height: "12.5px", // 8 * 1.5
                                margin: "0 16px",
                                alignSelf: "center",
                                transform: "translateY(1px)", // 👈 move DOWN
                              }}
                            />
                            <FlexOrderCarousel
                              shift={false}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "flex-start",
                                height: "100%",
                              }}
                            >
                              {currentDisplaySelectionData.map(
                                (item, index) => (
                                  <Typography
                                    variant="v2_special"
                                    sx={{
                                      color: item.active
                                        ? colors.text.v4
                                        : colors.text.v2,
                                      whiteSpace: "pre",
                                      fontSize: "10px !important",
                                    }}
                                  >
                                    {item.text}
                                  </Typography>
                                ),
                              )}
                            </FlexOrderCarousel>
                          </>
                        )}
                      </Box>
                      {isMobileDevice && (
                        <Box
                          sx={{
                            marginTop: "auto",
                            ...(shouldEmitData && {
                              marginBottom: "auto",
                            }),
                          }}
                        >
                          {ContactUsLinkMemo}
                        </Box>
                      )}
                    </>
                  )}
                  {!isMobileDevice && (
                    <Typography
                      sx={(theme) => ({
                        lineHeight: 1.3,
                        whiteSpace: "pre",
                        display: isMobileDevice ? "none" : "flex",
                        marginTop: {
                          [BREAKPOINT_MOBILE]: "38px",
                          xxxl: "66px",
                        },
                        marginBottom: {
                          [BREAKPOINT_MOBILE]: "38px",
                          xxxl: "74px",
                        },
                      })}
                      variant={"v2_special"}
                    >
                      {
                        "For more personalized\noptions please contact\nour sales team"
                      }
                    </Typography>
                  )}
                </Box>
              }

              <Box
                sx={{
                  minWidth: 0,
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <GoodsList
                  activeIndex={activeIndex}
                  setIndexForGroup={setIndexForGroup}
                />
              </Box>

              <Box
                ref={rightRef}
                sx={{
                  minWidth: 0, // ✅ CRITICAL
                  width: "auto",
                  display: "flex",
                  flexDirection: "column",
                  alignSelf: isMobileDevice ? "flex-end" : "center",
                  justifyContent: isMobileDevice ? "space-between" : "unset",
                  paddingTop: isMobileDevice ? "10px" : 0,
                  height: "100%",
                }}
              >
                {PriceBlockMemo}
                {isMobileDevice && (
                  <Box sx={{ marginTop: "0px" }}>{ReceivePDFMemo}</Box>
                )}
              </Box>
            </Box>
            {/* Textures/Extra list | END */}
          </Box>
        </Box>

        {/* Forms CTA | START */}
        <Box
          sx={getFluidsByKeysFunc(
            {
              display: "flex",
              justifyContent: "space-between",
            },
            [],
          )}
        >
          {!isMobileDevice && (
            <>
              {ContactUsLinkMemo}
              {ReceivePDFMemo}
            </>
          )}
        </Box>
        {/* Forms CTA | END */}
      </ConditionalWrapper>

      {/* Forms | START */}
      <ContactForm
        open={showContactForm && !sendingLeadData}
        disabled={sendingLeadData}
        onClose={() => setShowContactForm(false)}
        onSubmit={(values) => handleSubmit(values, false)}
      />

      <ReceivePdfForm
        open={showPdfForm && !sendingLeadData}
        disabled={sendingLeadData}
        onClose={() => {
          setShowPdfForm(false);
          setFloatingPointsVisible(true);
        }}
        // banner={formBanner}
        banner={formBanner}
        onSubmit={(values) => handleSubmit(values, true)}
      />
      {/* Forms | END */}

      <Dialog
        open={showChangeModelDialog}
        onClose={() => setShowChangeModelDialog(false)}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {Object.entries(MODELS_DATA).map(([key, value]) => (
              <BrandLink
                key={`change-model-${key}`}
                typographyProps={{
                  sx: {
                    color:
                      value.uid === activeProduct ? colors.text.v4 : undefined,
                    cursor: value.uid === activeProduct ? "default" : "pointer",
                  },
                }}
                onClick={() => {
                  if (value.uid === activeProduct) return;
                  setShowChangeModelDialog(false);
                  setActiveProduct(value.uid);
                }}
              >
                {value.title}
              </BrandLink>
            ))}
          </Box>

          <IconClose
            sx={{
              color: colors.text.v2,
              cursor: "pointer",
              alignSelf: "end",
              transition: transitions.join("color"),
              marginTop: "30px",
              marginLeft: "auto",
              "&:hover": { color: colors.text.v3 },
            }}
            onClick={() => setShowChangeModelDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </StyledApp>
  );
}

export default App;
